<script setup lang="ts">
import { ref } from 'vue'
import { useAuthStore } from '../stores/auth'

const store = useAuthStore()
const activeTab = ref('personal')

const tabs = [
  { id: 'personal', label: 'Personal Info', icon: '👤' },
  { id: 'security', label: 'Security', icon: '🔒' },
  { id: 'preferences', label: 'Preferences', icon: '⚙️' },
  { id: 'activity', label: 'Activity', icon: '📊' }
]

const personalInfo = ref({
  name: store.user?.name || '',
  email: store.user?.email || '',
  phone: store.user?.mobile_number || '',
  position: 'Manager',
  department: 'Sales',
  location: 'New York'
})

const securityInfo = ref({
  lastLogin: '2024-01-15 09:30 AM',
  lastPasswordChange: '2023-12-01',
  twoFactorEnabled: false
})

const preferences = ref({
  language: 'English',
  timezone: 'UTC-5',
  dateFormat: 'MM/DD/YYYY',
  notifications: {
    email: true,
    push: true,
    desktop: false
  }
})

const recentActivity = [
  { id: 1, type: 'login', description: 'Logged in from Chrome on Windows', date: '2024-01-15 09:30 AM' },
  { id: 2, type: 'settings', description: 'Updated profile information', date: '2024-01-14 03:15 PM' },
  { id: 3, type: 'security', description: 'Changed password', date: '2024-01-01 11:20 AM' },
  { id: 4, type: 'login', description: 'Logged in from Safari on macOS', date: '2023-12-31 02:45 PM' },
  { id: 5, type: 'settings', description: 'Updated notification preferences', date: '2023-12-30 10:10 AM' }
]

const handleSave = () => {
  // Handle saving profile changes
  console.log('Saving profile changes:', {
    personal: personalInfo.value,
    preferences: preferences.value
  })
}
</script>

<template>
  <div class="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-100 dark:border-gray-800">
    <!-- Header with Avatar -->
    <div class="px-6 py-8 border-b border-gray-100 dark:border-gray-800">
      <div class="flex items-center gap-6">
        <div class="relative group">
          <div class="w-24 h-24 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center text-4xl">
            👤
          </div>
          <button class="absolute inset-0 flex items-center justify-center bg-black/50 text-white opacity-0 group-hover:opacity-100 rounded-full transition-opacity">
            Change
          </button>
        </div>
        <div>
          <h1 class="text-2xl font-bold text-gray-900 dark:text-white">{{ personalInfo.name }}</h1>
          <p class="text-gray-600 dark:text-gray-400">{{ personalInfo.email }}</p>
        </div>
      </div>
    </div>

    <div class="flex min-h-[400px]">
      <!-- Tabs -->
      <div class="w-64 border-r border-gray-100 dark:border-gray-800">
        <nav class="p-4">
          <ul class="space-y-1">
            <li v-for="tab in tabs" :key="tab.id">
              <button
                @click="activeTab = tab.id"
                class="w-full px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
                :class="[
                  activeTab === tab.id
                    ? 'bg-blue-50 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                ]"
              >
                <span>{{ tab.icon }}</span>
                {{ tab.label }}
              </button>
            </li>
          </ul>
        </nav>
      </div>

      <!-- Content -->
      <div class="flex-1 p-6">
        <!-- Personal Info -->
        <div v-if="activeTab === 'personal'" class="space-y-6">
          <div class="grid grid-cols-2 gap-6">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Full Name
              </label>
              <input
                v-model="personalInfo.name"
                type="text"
                class="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email
              </label>
              <input
                v-model="personalInfo.email"
                type="email"
                class="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Phone
              </label>
              <input
                v-model="personalInfo.phone"
                type="tel"
                class="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Position
              </label>
              <input
                v-model="personalInfo.position"
                type="text"
                class="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Department
              </label>
              <input
                v-model="personalInfo.department"
                type="text"
                class="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Location
              </label>
              <input
                v-model="personalInfo.location"
                type="text"
                class="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
              />
            </div>
          </div>
        </div>

        <!-- Security -->
        <div v-else-if="activeTab === 'security'" class="space-y-6">
          <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
            <div class="flex items-center justify-between">
              <div>
                <h3 class="text-sm font-medium text-gray-900 dark:text-white">Two-Factor Authentication</h3>
                <p class="text-sm text-gray-500 dark:text-gray-400">Add an extra layer of security to your account</p>
              </div>
              <label class="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" v-model="securityInfo.twoFactorEnabled" class="sr-only peer">
                <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>

          <div class="space-y-4">
            <div class="flex justify-between py-4 border-b border-gray-100 dark:border-gray-700">
              <div>
                <h3 class="text-sm font-medium text-gray-900 dark:text-white">Last Login</h3>
                <p class="text-sm text-gray-500 dark:text-gray-400">{{ securityInfo.lastLogin }}</p>
              </div>
              <button class="text-sm text-blue-600 dark:text-blue-400">View All</button>
            </div>

            <div class="flex justify-between py-4 border-b border-gray-100 dark:border-gray-700">
              <div>
                <h3 class="text-sm font-medium text-gray-900 dark:text-white">Password</h3>
                <p class="text-sm text-gray-500 dark:text-gray-400">Last changed {{ securityInfo.lastPasswordChange }}</p>
              </div>
              <button class="text-sm text-blue-600 dark:text-blue-400">Change</button>
            </div>
          </div>
        </div>

        <!-- Preferences -->
        <div v-else-if="activeTab === 'preferences'" class="space-y-6">
          <div class="grid grid-cols-2 gap-6">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Language
              </label>
              <select
                v-model="preferences.language"
                class="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
              >
                <option>English</option>
                <option>Spanish</option>
                <option>French</option>
              </select>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Time Zone
              </label>
              <select
                v-model="preferences.timezone"
                class="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
              >
                <option>UTC</option>
                <option>UTC-5</option>
                <option>UTC+1</option>
              </select>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Date Format
              </label>
              <select
                v-model="preferences.dateFormat"
                class="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
              >
                <option>MM/DD/YYYY</option>
                <option>DD/MM/YYYY</option>
                <option>YYYY-MM-DD</option>
              </select>
            </div>
          </div>

          <div class="space-y-4">
            <h3 class="text-sm font-medium text-gray-700 dark:text-gray-300">Notifications</h3>
            
            <div class="space-y-3">
              <label class="flex items-center">
                <input
                  type="checkbox"
                  v-model="preferences.notifications.email"
                  class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">Email Notifications</span>
              </label>

              <label class="flex items-center">
                <input
                  type="checkbox"
                  v-model="preferences.notifications.push"
                  class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">Push Notifications</span>
              </label>

              <label class="flex items-center">
                <input
                  type="checkbox"
                  v-model="preferences.notifications.desktop"
                  class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">Desktop Notifications</span>
              </label>
            </div>
          </div>
        </div>

        <!-- Activity -->
        <div v-else-if="activeTab === 'activity'" class="space-y-6">
          <div class="space-y-4">
            <div
              v-for="activity in recentActivity"
              :key="activity.id"
              class="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg"
            >
              <div class="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center">
                {{ 
                  activity.type === 'login' ? '🔑' :
                  activity.type === 'settings' ? '⚙️' :
                  activity.type === 'security' ? '🔒' : '📝'
                }}
              </div>
              <div>
                <p class="text-sm text-gray-900 dark:text-white">{{ activity.description }}</p>
                <p class="text-xs text-gray-500 dark:text-gray-400">{{ activity.date }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="px-6 py-4 border-t border-gray-100 dark:border-gray-800 flex justify-end">
      <button
        @click="handleSave"
        class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
      >
        Save Changes
      </button>
    </div>
  </div>
</template>